import { useEffect, useState, type RefObject } from 'react';

/**
 * True once the element has come within `margin` of the viewport, and true
 * from then on. Used to hold back a fetch until something is about to be seen.
 */
export function useInView(ref: RefObject<Element | null>, margin = '200px'): boolean {
  // Without IntersectionObserver there is no way to wait, so load straight away.
  const [seen, setSeen] = useState(() => typeof IntersectionObserver === 'undefined');

  useEffect(() => {
    const el = ref.current;
    if (seen || !el) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setSeen(true);
          observer.disconnect();
        }
      },
      { rootMargin: margin },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [ref, margin, seen]);

  return seen;
}
